import React, { useState, useEffect, useCallback } from 'react';
import styles from './Home.module.css';
import UserActivityComponent from './UserActivity';
import UserQRCodePopup from '../QRCodePopup/UserQRCodePopup';
import { authService } from '../../services/auth.service';
import {
  getDashboardData,
  DashboardData,
} from '../../services/dashboard.service';
import { getBrandSiteUrl } from '../../utils/getDomainBrand';
import { useEnvironment } from '../../contexts/EnvironmentContext';

interface CashFlowChartProps {
  data: DashboardData;
}

const CashFlowChart: React.FC<CashFlowChartProps> = ({ data }) => {
  const periods = [
    { label: 'Today', in: data.sumCashInToday, out: data.sumCashoutToday },
    { label: 'Yesterday', in: data.sumCashInYesterday, out: data.sumCashoutYesterday },
    { label: '7 Days', in: data.sumCashInLast7Days, out: data.sumCashoutLast7Days },
    { label: '30 Days', in: data.sumCashInLast30Days, out: data.sumCashoutLast30Days },
  ];
  const max = Math.max(1, ...periods.flatMap((p) => [p.in, p.out]));
  const totalIn = periods[3].in;
  const totalOut = periods[3].out;
  const totalVolume = totalIn + totalOut;
  const inPct = totalVolume > 0 ? (totalIn / totalVolume) * 100 : 50;
  const donutCircumference = 2 * Math.PI * 52;
  const inDash = (inPct / 100) * donutCircumference;

  const formatMoney = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return `${n.toFixed(0)}`;
  };

  return (
    <div className={styles['chart-section']}>
      <div className={styles['chart-card']}>
        <div className={styles['chart-header']}>
          <div>
            <h3 className={styles['chart-title']}>Cash Flow</h3>
            <p className={styles['chart-subtitle']}>Recharge vs Withdrawal across periods</p>
          </div>
          <div className={styles['chart-legend']}>
            <span className={styles['legend-item']}>
              <span className={`${styles['legend-dot']} ${styles['legend-in']}`}></span>
              Recharge
            </span>
            <span className={styles['legend-item']}>
              <span className={`${styles['legend-dot']} ${styles['legend-out']}`}></span>
              Withdrawal
            </span>
          </div>
        </div>
        <div className={styles['chart-bars']}>
          {periods.map((p) => {
            const inH = (p.in / max) * 100;
            const outH = (p.out / max) * 100;
            return (
              <div key={p.label} className={styles['chart-group']}>
                <div className={styles['chart-bar-pair']}>
                  <div
                    className={`${styles['chart-bar']} ${styles['bar-in']}`}
                    style={{ height: `${inH}%` }}
                    title={`Recharge: ${p.in.toFixed(2)}`}
                  >
                    <span className={styles['bar-value']}>{formatMoney(p.in)}</span>
                  </div>
                  <div
                    className={`${styles['chart-bar']} ${styles['bar-out']}`}
                    style={{ height: `${outH}%` }}
                    title={`Withdrawal: ${p.out.toFixed(2)}`}
                  >
                    <span className={styles['bar-value']}>{formatMoney(p.out)}</span>
                  </div>
                </div>
                <div className={styles['chart-label']}>{p.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles['donut-card']}>
        <h3 className={styles['chart-title']}>30-Day Ratio</h3>
        <div className={styles['donut-wrap']}>
          <svg viewBox="0 0 120 120" className={styles['donut-svg']}>
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="url(#gradOut)"
              strokeWidth="14"
            />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="url(#gradIn)"
              strokeWidth="14"
              strokeDasharray={`${inDash} ${donutCircumference}`}
              strokeDashoffset={donutCircumference / 4}
              transform="rotate(-90 60 60)"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="gradIn" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#3eaa7e" />
                <stop offset="100%" stopColor="#2e9968" />
              </linearGradient>
              <linearGradient id="gradOut" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#f0d8de" />
                <stop offset="100%" stopColor="#e8c7d0" />
              </linearGradient>
            </defs>
          </svg>
          <div className={styles['donut-center']}>
            <div className={styles['donut-pct']}>{Math.round(inPct)}%</div>
            <div className={styles['donut-sub']}>Recharge</div>
          </div>
        </div>
        <div className={styles['donut-stats']}>
          <div>
            <span className={styles['donut-dot-in']}></span> In {formatMoney(totalIn)}
          </div>
          <div>
            <span className={styles['donut-dot-out']}></span> Out {formatMoney(totalOut)}
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [qrCodeSiteUrl, setQrCodeSiteUrl] = useState<string>('');
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [qrCodeError, setQrCodeError] = useState<string>('');
  const { environments, selectedEnvironmentId, setSelectedEnvironmentId, isAdmin } = useEnvironment();

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 820);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Get current user
  useEffect(() => {
    const currentUser = authService.getStoredUser();
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  // Generate QR code when user is available
  const generateQRCode = useCallback(async () => {
    if (!user?.id) return;

    setQrCodeLoading(true);
    setQrCodeError('');

    try {
      const { QRCodeService } = await import('../../services/qrCode.service');
      const qrService = QRCodeService.getInstance();

      // Generate QR code with user ID
      // await qrService.generateQRCode
      const dataUrl = await qrService.generateQRCode(user.id.toString());
      const siteUrl = await qrService.generateQRCode(`${getBrandSiteUrl()}/`, true);
      setQrCodeDataUrl(dataUrl);
      setQrCodeSiteUrl(siteUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setQrCodeError('Failed to generate QR code. Please try again.');
    } finally {
      setQrCodeLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      generateQRCode();
    }
  }, [user?.id, generateQRCode]);

  // Fetch dashboard data when environment changes
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const envId = selectedEnvironmentId ? Number(selectedEnvironmentId) : undefined;
        const data = await getDashboardData(envId);
        setDashboardData(data);
      } catch (err) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedEnvironmentId]);

  const handleQRCodeModalOpen = () => {
    setIsQRCodeModalOpen(true);
  };

  const handleQRCodeModalClose = () => {
    setIsQRCodeModalOpen(false);
  };

  const handleDownloadQRCode = () => {
    if (qrCodeDataUrl && user?.id) {
      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = `user-qr-${user.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyUserId = async () => {
    if (user?.id) {
      try {
        await navigator.clipboard.writeText(user.id.toString());
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const formatInt = (n: number) => Number(n || 0).toLocaleString('en-US');
  const formatMoney = (n: number) =>
    Number(n || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className={styles['content-wrapper']}>
      {/* Main Content */}
      <main className={styles['main-content']}>
        {isAdmin && (
          <div className={styles['environment-selector']}>
            <label className={styles['environment-label']}>Environment</label>
            <select
              className={styles['environment-select']}
              value={selectedEnvironmentId}
              onChange={(e) => setSelectedEnvironmentId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All Environments</option>
              {environments.map((env) => (
                <option key={env.id} value={env.id}>
                  {env.name} - {env.host}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading && (
          <div className={styles['loading-container']}>
            Loading dashboard data...
          </div>
        )}

        {error && <div className={styles['error-container']}>{error}</div>}

        {!loading && !error && dashboardData && (
          <section className={styles['dashboard-top']}>
            <div className={styles['dashboard-cards']}>
              <div className={`${styles['dashboard-card']} ${styles['kpi-players']}`}>
                <div className={styles['kpi-icon']}>👥</div>
                <div className={styles['kpi-label']}>Total Players Registered</div>
                <span>{formatInt(dashboardData.countOfPlayersUnderMe)}</span>
              </div>
              <div className={`${styles['dashboard-card']} ${styles['kpi-new']}`}>
                <div className={styles['kpi-icon']}>✨</div>
                <div className={styles['kpi-label']}>New Users Today</div>
                <span>{formatInt(dashboardData.newPlayersToday)}</span>
              </div>
              <div className={`${styles['dashboard-card']} ${styles['kpi-withdrawals']}`}>
                <div className={styles['kpi-icon']}>💸</div>
                <div className={styles['kpi-label']}>Withdrawals Yesterday</div>
                <span>{formatMoney(dashboardData.sumCashoutYesterday)}</span>
              </div>
            </div>

            <CashFlowChart data={dashboardData} />
          </section>
        )}
        {!loading && !error && dashboardData && (
          <section className={styles['latest-updates']}>
            <h2>Latest Updates</h2>

            <div className={styles['updates-row']}>
              <div className={`${styles['update-row-card']} ${styles['cash-in']}`}>
                <div className={styles['update-card-arrow']}>↗</div>
                Recharge (30 Days)
                <span>{formatMoney(dashboardData.sumCashInLast30Days)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-out']}`}>
                <div className={styles['update-card-arrow']}>↘</div>
                Withdrawal (30 Days)
                <span>{formatMoney(dashboardData.sumCashoutLast30Days)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-in']}`}>
                <div className={styles['update-card-arrow']}>↗</div>
                Recharge Yesterday
                <span>{formatMoney(dashboardData.sumCashInYesterday)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-out']}`}>
                <div className={styles['update-card-arrow']}>↘</div>
                Withdrawal Yesterday
                <span>{formatMoney(dashboardData.sumCashoutYesterday)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-in']}`}>
                <div className={styles['update-card-arrow']}>↗</div>
                Recharge Today
                <span>{formatMoney(dashboardData.sumCashInToday)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-out']}`}>
                <div className={styles['update-card-arrow']}>↘</div>
                Withdrawal Today
                <span>{formatMoney(dashboardData.sumCashoutToday)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-in']}`}>
                <div className={styles['update-card-arrow']}>↗</div>
                Recharge (7 Days)
                <span>{formatMoney(dashboardData.sumCashInLast7Days)}</span>
              </div>
              <div className={`${styles['update-row-card']} ${styles['cash-out']}`}>
                <div className={styles['update-card-arrow']}>↘</div>
                Withdrawal (7 Days)
                <span>{formatMoney(dashboardData.sumCashoutLast7Days)}</span>
              </div>
            </div>
            <div className={styles['updates-cards']}>
              <div className={styles['update-card']}>
                {qrCodeLoading ? (
                  <div className={styles['qr-code-container']}>
                    <div className={styles['qr-code-spinner']}></div>
                    <span>Generating QR Code...</span>
                  </div>
                ) : qrCodeError ? (
                  <div className={styles['qr-code-error']}>
                    <span>Failed to generate QR code</span>
                    <br />
                    <button
                      onClick={generateQRCode}
                      className={styles['qr-code-error']}
                    >
                      Retry
                    </button>
                  </div>
                ) : qrCodeDataUrl ? (
                  <div className={styles['qr-code-container']}>
                    <img
                      src={qrCodeDataUrl}
                      alt="User QR Code"
                      className={styles['qr-code-image']}
                    />
                    <br />
                    <span className={styles['qr-code-label']}>
                      Your Affiliate QR Code
                    </span>
                    <br />
                    <div className={styles['qr-code-actions']}>
                      <button
                        onClick={handleDownloadQRCode}
                        className={`${styles['qr-code-button']} ${styles['qr-code-button-download']}`}
                      >
                        Download
                      </button>
                      <button
                        onClick={handleCopyUserId}
                        className={`${styles['qr-code-button']} ${styles['qr-code-button-copy']}`}
                      >
                        Copy ID
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    Generate QR Code
                    <br />
                    <span>📱</span>
                  </div>
                )}
                {qrCodeLoading ? (
                  <div className={styles['qr-code-container']}>
                    <div className={styles['qr-code-spinner']}></div>
                    <span>Generating QR Code...</span>
                  </div>
                ) : qrCodeError ? (
                  <div className={styles['qr-code-error']}>
                    <span>Failed to generate QR code</span>
                    <br />
                    <button
                      onClick={generateQRCode}
                      className={styles['qr-code-error']}
                    >
                      Retry
                    </button>
                  </div>
                ) : qrCodeSiteUrl ? (
                  <div className={styles['qr-code-container']}>
                    <img
                      src={qrCodeSiteUrl}
                      alt="User QR Code"
                      className={styles['qr-code-image']}
                    />
                    <br />
                    <span className={styles['qr-code-label']}>
                      Scan QR Code For Downloading App
                    </span>
                    <br />
                    <div className={styles['qr-code-actions']}>
                      <button
                        onClick={() => window.open(getBrandSiteUrl(), '_blank')}
                        className={`${styles['qr-code-button']} ${styles['qr-code-button-download']}`}
                      >
                        Open Site
                      </button>

                    </div>
                  </div>
                ) : (
                  <div>
                    Generate QR Code
                    <br />
                    <span>📱</span>
                  </div>
                )}
              </div>
              {/* <div className={styles['update-card']}>
                Accumulated Withdrawal (30 Days)
                <br />
                <span>${dashboardData.sumCashoutLast30Days.toFixed(2)}</span>
              </div> */}
              {/* <div className={styles['update-card']}>
                Recharge Amount Yesterday
                <br />
                <span>${dashboardData.sumCashInYesterday.toFixed(2)}</span>
              </div> */}
            </div>
          </section>
        )}

        {/* Mobile User Activity Section */}
        {isMobile && (
          <section className={styles['mobile-user-activity']}>
            <UserActivityComponent />
          </section>
        )}
      </main>

      {/* Right Info Panel */}
      <aside className={styles['right-panel']}>
        {/* Desktop User Activity Section */}
        {!isMobile && <UserActivityComponent />}

        {/* {!loading && !error && dashboardData && (
          <div className={styles['right-panel-cards']}>
            <div className={styles['right-card']}>
              New Players Today
              <br />
              <span>{dashboardData.newPlayersToday}</span>
            </div>
            <div className={styles['right-card']}>
              Amount Withdrawn Yesterday
              <br />
              <span>${dashboardData.sumCashoutYesterday.toFixed(2)}</span>
            </div>
            <div className={styles['right-card']}>
              Cash In Yesterday
              <br />
              <span>${dashboardData.sumCashInYesterday.toFixed(2)}</span>
            </div>
          </div>
        )} */}
      </aside>

      {/* User QR Code Modal */}
      {user && (
        <UserQRCodePopup
          isOpen={isQRCodeModalOpen}
          onClose={handleQRCodeModalClose}
          userId={user.id.toString()}
          username={user.username}
          name={user.name}
        />
      )}
    </div>
  );
};

export default HomePage;
